import { deriveHomeRecommendations, checkpointEndMs } from './homeRecommendations';
import { slotStarted } from './definitions';

export const dateLabel = (date) => {
  if (!date || !Number.isFinite(Date.parse(date))) return 'No due date';
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T12:00:00` : date)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short' });
};

// A practice set's start (2026-10-09): teachers schedule several sets on one
// day, so the time of day matters ("Fri, Oct 9, 3:14 PM").
export const startLabel = (iso) => {
  if (!iso || !Number.isFinite(Date.parse(iso))) return '';
  return new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// Use the same reconciled slots as Monitor. Missing/failed reads never become
// "not started", and future work never counts as overdue or as incomplete now.
export function teacherHomeModel(snapshot, now = Date.now()) {
  const model = deriveHomeRecommendations(snapshot, { now });
  const unit = snapshot?.unit;
  const dates = unit?.dueDates || [];
  const validDates = dates.length > 0 && dates.every(d => Number.isFinite(checkpointEndMs(d)));
  const lastDue = validDates ? dates[dates.length - 1] : null;
  const endedByDate = !!lastDue && checkpointEndMs(lastDue) < now;
  const nextIndex = dates.findIndex(d => checkpointEndMs(d) >= now);
  const focusIndex = validDates ? (nextIndex < 0 ? dates.length - 1 : nextIndex) : -1;
  const groups = { done: [], working: [], notStarted: [], unknown: [], excused: [], help: [], placement: [], advanced: [] };
  const rosterKnown = !snapshot?.stale && snapshot?.completeness?.roster === true;
  const slotsKnown = rosterKnown && snapshot?.completeness?.slots === true && snapshot?.completeness?.units === true;
  const rows = [...new Map((snapshot?.students || []).filter(s => s.userId).map(s => [s.userId, s])).values()];
  let compared = 0;
  rows.forEach(s => {
    const fact = model.facts?.students?.[s.userId];
    const slot = fact?.slots?.[focusIndex];
    if (!slotsKnown || !slot || slot.status === 'unknown') groups.unknown.push(s);
    else if (slot.skipped || slot.applies === false) groups.excused.push(s);
    else if (slot.done) groups.done.push(s);
    else if (slotStarted(slot)) groups.working.push(s);
    else groups.notStarted.push(s);
    if (rosterKnown && fact?.stuck === true) groups.help.push(s);
    if (rosterKnown && ['needsPlacement', 'checkingPlacement', 'missingProfile'].includes(fact?.placementStatus)) groups.placement.push({ ...s, placementStatus: fact.placementStatus });
    // A whole-field unit compares each student in their own domain (unitDomain).
    const d = s.domains?.[model.facts.fieldUnit ? s.unitDomain : model.summary.domainKey];
    if (rosterKnown && snapshot?.completeness?.profiles === true && s.profileStatus === 'ready'
      && Number.isInteger(d?.initialBand) && d.initialBand >= 1 && d.initialBand <= 7
      && Number.isInteger(d?.band) && d.band >= 1 && d.band <= 7) {
      compared += 1;
      if (d.band > d.initialBand) groups.advanced.push(s);
    }
  });
  const total = rows.length - groups.excused.length;
  let summary = 'I’m checking how your class is doing.';
  if (rosterKnown && rows.length === 0) summary = 'Your unit is ready. Students will appear here as they join your class.';
  else if (slotsKnown && focusIndex >= 0) {
    summary = `${groups.done.length} of ${total} students have finished the work due ${dateLabel(dates[focusIndex])}.`;
    if (groups.unknown.length) summary += ` I’m missing an update for ${groups.unknown.length}.`;
    else if (groups.working.length) summary += ` ${groups.working.length} ${groups.working.length === 1 ? 'is' : 'are'} still working on it.`;
  } else if (snapshot) summary = 'Some class updates are unavailable. I’ll show confirmed details as they arrive.';
  return { ...model, rows, groups, total, compared, summary, focusIndex, focusDue: dates[focusIndex], lastDue, endedByDate, rosterKnown, slotsKnown };
}
